import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Paper, Typography, Chip, Button, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, CircularProgress, Alert,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import HeaderBannerPage from "../../components/HeaderBannerPage";
import {
  NAVY, cardSx, headCellSx, EmployeeSelect, RequestStatusChip, fmtDate,
} from "../../components/leave/LeaveCommon";
import { getAllEmployees } from "../../actions/EmployeeRegisterAction";
import { fetchLeaveTypes } from "../../actions/Leave/LeaveTypeAction";
import { fetchLeaveRequests } from "../../actions/Leave/LeaveRequestAction";
import { extractRows } from "../../service/Leave/leaveHelpers";

const ROWS = 7;
const STATUS_TABS = [
  { id: "All", label: "All" },
  { id: "Pending", label: "Pending" },
  { id: "Approved", label: "Approved" },
  { id: "Rejected", label: "Rejected" },
];

const LeaveHistory = () => {
  const dispatch = useDispatch();

  // ── Redux data ──────────────────────────────────────────────
  const { data: rawEmployees } = useSelector((s) => s.getAllEmployees) || {};
  const { data: leaveTypes = [] } = useSelector((s) => s.leaveTypes) || {};
  const { data: records = [], loading, error } = useSelector((s) => s.leaveRequests) || {};

  // ── Filters ─────────────────────────────────────────────────
  const [employee, setEmployee] = useState(null); // null = all employees
  const [status, setStatus] = useState("Pending");
  const [page, setPage] = useState(0);

  // Employee list + leave types for names; the three request lists load once
  useEffect(() => {
    dispatch(getAllEmployees());
    dispatch(fetchLeaveTypes());
    dispatch(fetchLeaveRequests());
  }, [dispatch]);

  const employeeOptions = useMemo(
    () =>
      extractRows(rawEmployees)
        .map((e) => {
          const serviceNo = String(e.serviceNo ?? e.ServiceNo ?? "").trim();
          return {
            serviceNo,
            name: e.reportName || e.ReportName || e.name || serviceNo,
            department: e.department || e.DepartmentCode || e.Designation || "",
          };
        })
        .filter((e) => e.serviceNo),
    [rawEmployees]
  );
  const empNameByNo = useMemo(() => Object.fromEntries(employeeOptions.map((e) => [e.serviceNo, e.name])), [employeeOptions]);
  const typeNameById = useMemo(() => Object.fromEntries(leaveTypes.map((t) => [t.id, t.name])), [leaveTypes]);

  // requests for the chosen employee, before the status tab is applied
  const byEmployee = useMemo(
    () => records.filter((r) => !employee || r.serviceNo === employee.serviceNo),
    [records, employee]
  );
  const counts = useMemo(() => {
    const c = { All: byEmployee.length, Pending: 0, Approved: 0, Rejected: 0 };
    byEmployee.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [byEmployee]);

  const filtered = useMemo(
    () =>
      byEmployee
        .filter((r) => status === "All" || r.status === status)
        .sort((a, b) => b.from.localeCompare(a.from)),
    [byEmployee, status]
  );
  const rows = filtered.slice(page * ROWS, page * ROWS + ROWS);

  const refresh = () => dispatch(fetchLeaveRequests());

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <HeaderBannerPage />
      <Box sx={{ maxWidth: 1400, mx: "auto", mt: 4, px: 2, pb: 4 }}>
        {/* Banner */}
        <Paper
          elevation={0}
          sx={{
            p: 3, mb: 3, borderRadius: "12px",
            background: "linear-gradient(135deg, #1e0153 0%, #154c79 100%)",
            color: "#fff", boxShadow: "0px 4px 12px rgba(30, 1, 83, 0.15)",
          }}
        >
          <Chip
            label="LEAVE MANAGEMENT" size="small"
            sx={{ fontWeight: "bold", mb: 1, fontSize: "11px", letterSpacing: "0.5px", backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" }}
          />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
            Leave History
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, fontSize: "13px" }}>
            All past and pending leave requests, across every leave type and status
          </Typography>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>Could not load leave requests: {String(error)}</Alert>}

        {/* Filters */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, alignItems: "center" }}>
          <EmployeeSelect
            label="Employee" placeholder="All Employees" options={employeeOptions} value={employee}
            onChange={(e) => { setEmployee(e); setPage(0); }} sx={{ width: 320 }}
          />
          <Button
            onClick={refresh} disabled={loading}
            startIcon={<RefreshIcon sx={loading ? { animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } } : {}} />}
            sx={{ textTransform: "none", fontWeight: 600, color: "#334155", border: "1px solid #e2e8f0", height: 40, px: 2 }}
          >
            Refresh
          </Button>
        </Box>

        {/* Status tabs */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Tabs
            value={status}
            onChange={(e, v) => { setStatus(v); setPage(0); }}
            variant="scrollable" scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: NAVY },
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
              "& .MuiTab-root.Mui-selected": { color: NAVY },
            }}
          >
            {STATUS_TABS.map((t) => (
              <Tab
                key={t.id} value={t.id}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {t.label}
                    <Chip label={counts[t.id]} size="small" sx={{ height: 18, fontSize: 11, fontWeight: 700, backgroundColor: "#f1f5f9", color: "#475569" }} />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>

        {/* Table */}
        <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  {["Form #", "Service No", "Employee", "Leave Type", "From", "To", "Days", "Status"].map((h) => (
                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}><CircularProgress size={26} /></TableCell>
                  </TableRow>
                )}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#94a3b8" }}>
                      No leave records found.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && rows.map((r) => (
                  <TableRow key={r.key} hover>
                    <TableCell sx={{ fontSize: 13, fontFamily: "monospace" }}>{r.formNo}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#64748b" }}>{r.serviceNo}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{empNameByNo[r.serviceNo] || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{typeNameById[r.leaveTypeId] || `Type ${r.leaveTypeId}`}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{fmtDate(r.from)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{fmtDate(r.to)}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>{r.days}</TableCell>
                    <TableCell><RequestStatusChip status={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && filtered.length > ROWS && (
            <TablePagination
              component="div" count={filtered.length} page={page} onPageChange={(e, p) => setPage(p)}
              rowsPerPage={ROWS} rowsPerPageOptions={[ROWS]}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default LeaveHistory;